import type { Alpha3Code } from 'i18n-iso-countries'
import { uniq } from 'lodash'

import { useServices } from '@percent/cause-dashboard/context/serviceContext/ServiceContext'
import type {
  VolunteeringActivityDto,
  VolunteeringSkillDto
} from '@percent/cause-dashboard/services/volunteering/volunteeringService.types'
import { useQuery } from '@percent/utility'
import { useMemo } from 'react'

export type VolunteeringPartnerRegion = Readonly<{
  id: string
  name: string
  countryCodes: Alpha3Code[]
}>

export type UsePartnerConfigResult = Readonly<{
  enabled: boolean
  isLoading: boolean
  errors?: string[]
  skills: VolunteeringSkillDto[]
  activities: VolunteeringActivityDto[]
  regions: Record<string, VolunteeringPartnerRegion>
  allCountries: Alpha3Code[]
}>

export const usePartnerConfig = (partnerId?: string): UsePartnerConfigResult => {
  const { volunteeringService } = useServices()

  const enabled = useMemo(() => !!partnerId, [partnerId])

  const {
    data: partnerSkills,
    isPending: isLoadingSkills,
    errorMessage: skillsErrorMessage
  } = useQuery(
    ['volunteering-partner-skills', partnerId],
    volunteeringService.getVolunteeringPartnerSkills,
    {
      partnerId: partnerId ?? ''
    },
    { enabled }
  )

  const {
    data: partnerActivities,
    isPending: isLoadingActivities,
    errorMessage: activitiesErrorMessage
  } = useQuery(
    ['volunteering-partner-activities', partnerId],
    volunteeringService.getVolunteeringPartnerActivities,
    {
      partnerId: partnerId ?? ''
    },
    { enabled }
  )

  const {
    data: regions,
    isPending: isLoadingRegions,
    errorMessage: regionsErrorMessage
  } = useQuery(
    ['volunteering-partner-regions', partnerId],
    volunteeringService.getVolunteeringPartnerRegions,
    {
      partnerId: partnerId ?? ''
    },
    { enabled }
  )

  const allCountries = useMemo(
    () => uniq(Object.values(regions ?? {}).flatMap(region => region.countryCodes)),
    [regions]
  )

  const isLoading = isLoadingSkills || isLoadingActivities || isLoadingRegions

  const errors = [skillsErrorMessage, activitiesErrorMessage, regionsErrorMessage].filter(Boolean) as string[]

  return {
    enabled,
    isLoading,
    errors,
    skills: partnerSkills ?? [],
    activities: partnerActivities ?? [],
    regions: regions ?? {},
    allCountries
  }
}
